/**
 * Typed API client (design §5.2). `schema.d.ts` is generated from the Rust
 * DTOs (`npm run gen:api`); never edit it by hand.
 *
 * @module
 */
import createClient, { type Middleware } from "openapi-fetch";
import type { components, paths } from "./schema";

export type Schemas = components["schemas"];
export type SessionInfo = Schemas["SessionInfo"];
export type UserSummary = Schemas["UserSummary"];
export type TeamSummary = Schemas["TeamSummary"];
export type TeamMember = Schemas["TeamMember"];
export type AuditEntry = Schemas["AuditEntry"];
export type AuditPage = Schemas["AuditPage"];
export type AuditVerifyReport = Schemas["AuditVerifyReport"];
export type Permission = Schemas["Permission"];
export type Role = Schemas["Role"];
export type UserPreferences = Schemas["UserPreferences"];
export type AgcPreferences = Schemas["AgcPreferences"];
export type ImportLibrary = Schemas["ImportLibrary"];
export type ImportItem = Schemas["ImportItem"];
export type ImportCounts = Schemas["ImportCounts"];
export type PendingUpload = Schemas["PendingUpload"];
export type FilenameProfile = Schemas["FilenameProfile"];
export type FilenameProfileInput = Schemas["FilenameProfileInput"];
export type ProfileMatch = Schemas["ProfileMatch"];
export type ProfileSuggestion = Schemas["ProfileSuggestion"];
export type TimelineLane = Schemas["TimelineLane"];
export type TimelineFile = Schemas["TimelineFile"];
export type TimelineEvent = Schemas["TimelineEvent"];
export type ReviewItem = Schemas["ReviewItem"];
export type CaseSummary = Schemas["CaseSummary"];
export type CaseDetail = Schemas["CaseDetail"];
export type CaseGrant = Schemas["CaseGrant"];
export type ProxyTiming = Schemas["ProxyTiming"];
export type ExportSummary = Schemas["ExportSummary"];
export type CheckpointStatus = Schemas["CheckpointStatus"];
export type RetentionClass = Schemas["RetentionClass"];
export type RetentionFile = Schemas["RetentionFile"];
export type DisposalSummary = Schemas["DisposalSummary"];

export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly code: string,
    message: string,
  ) {
    super(message);
    this.name = "ApiError";
  }
}

let csrfToken: string | null = null;

/** Set from SessionInfo after login or /me; cleared on logout. */
export function setCsrfToken(token: string | null): void {
  csrfToken = token;
}

/** For requests made outside `api`, such as TUS uploads. */
export function getCsrfToken(): string | null {
  return csrfToken;
}

const csrfMiddleware: Middleware = {
  onRequest({ request }) {
    if (csrfToken && !["GET", "HEAD", "OPTIONS"].includes(request.method)) {
      request.headers.set("X-CSRF-Token", csrfToken);
    }
    return request;
  },
};

export const api = createClient<paths>({ baseUrl: "", credentials: "same-origin" });
api.use(csrfMiddleware);

type FetchResult<T> = { data?: T; error?: unknown; response: Response };

/** Returns the data or throws an ApiError with the server's error code. */
export async function unwrap<T>(promise: Promise<FetchResult<T>>): Promise<T> {
  const { data, error, response } = await promise;
  if (!response.ok) {
    const body = (error ?? {}) as { error?: string; message?: string };
    throw new ApiError(
      response.status,
      body.error ?? "http_error",
      body.message ?? `${response.status} ${response.statusText}`,
    );
  }
  return data as T;
}
